import { useState, type FormEvent } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Modal from "Common/Components/Modal";
import ModalData from "Common/Components/ModalDate/ModalData";
import { apiErrorMessage } from "Common/constants/apiErrors";

const BASE_URL = "https://portal-api.mahanpanel.com";

interface ConfirmAccountingPaymentModalProps {
  show: boolean;
  invoiceId: number | null;
  paymentId: number | null;
  onHide: () => void;
  onSuccess: () => void;
}

const ConfirmAccountingPaymentModal = ({
  show,
  invoiceId,
  paymentId,
  onHide,
  onSuccess,
}: ConfirmAccountingPaymentModalProps) => {
  const navigate = useNavigate();
  const [trackingCode, setTrackingCode] = useState("");
  const [depositDate, setDepositDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!invoiceId || !paymentId) return;
    if (!trackingCode.trim() || !depositDate.trim()) {
      Swal.fire("خطا", "کد رهگیری و تاریخ واریز الزامی است", "error");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      await axios.post(
        `${BASE_URL}/proforma-invoices/${invoiceId}/payments/${paymentId}/confirm-accounting`,
        { tracking_code: trackingCode.trim(), deposit_date: depositDate },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTrackingCode("");
      setDepositDate("");
      onHide();
      onSuccess();
      window.dispatchEvent(new Event("mahan:refresh-menu-counts"));
      Swal.fire("عملیات موفق", "پرداخت با موفقیت تأیید شد", "success");
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      Swal.fire("خطا", apiErrorMessage(error, "خطا در تأیید پرداخت"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} modal-center="true" dialogClassName="w-screen md:w-[32rem]">
      <Modal.Header
        className="flex items-center justify-between p-4 border-b dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-blue-400 hover:text-red-500"
      >
        <Modal.Title className="text-16">تأیید پرداخت توسط حسابداری</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="accounting-payment-tracking-code" className="block text-sm font-medium mb-1">
              کد رهگیری:
            </label>
            <input
              id="accounting-payment-tracking-code"
              value={trackingCode}
              onChange={(event) => setTrackingCode(event.target.value)}
              className="form-input w-full"
              required
            />
          </div>
          <ModalData data={depositDate} setData={setDepositDate} label="تاریخ واریز:" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onHide} className="btn border border-slate-300" disabled={submitting}>
              انصراف
            </button>
            <button type="submit" className="btn bg-custom-500 text-white" disabled={submitting}>
              {submitting ? "در حال ثبت..." : "تأیید پرداخت"}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default ConfirmAccountingPaymentModal;
